import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Plus, Edit, Trash2, ChevronRight, FolderTree, List, Search } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { useToast } from "../../components/ui/use-toast";
import {
  fetchCategories,
  deleteCategory,
  clearError,
} from "../../redux/slices/categorySlice";
import CategoryDialog from "./CategoryDialog";
import CategoryQuestionsDialog from "./CategoryQuestionsDialog";

function categoryMatchesSearch(category, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  const name = (category.name?.en || category.name || "").toString().toLowerCase();
  const desc = (
    category.description?.en ||
    category.description ||
    ""
  )
    .toString()
    .toLowerCase();
  if (name.includes(q) || desc.includes(q)) return true;
  return (category.children || []).some((child) =>
    categoryMatchesSearch(child, query)
  );
}

function filterCategoryTree(items, query) {
  if (!query) return items;
  return items
    .map((cat) => {
      const children = filterCategoryTree(cat.children || [], query);
      if (categoryMatchesSearch(cat, query) || children.length > 0) {
        return { ...cat, children };
      }
      return null;
    })
    .filter(Boolean);
}

function Categories() {
  const dispatch = useDispatch();
  const { categories, loading, error } = useSelector((state) => state.category);
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [questionsDialogOpen, setQuestionsDialogOpen] = useState(false);
  const [questionsCategory, setQuestionsCategory] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error,
        variant: "destructive",
      });
      dispatch(clearError());
    }
  }, [error, toast, dispatch]);

  // Flatten categories for dialog parent selection
  const flattenCategories = (cats, result = []) => {
    cats.forEach((cat) => {
      result.push(cat);
      if (cat.children && cat.children.length > 0) {
        flattenCategories(cat.children, result);
      }
    });
    return result;
  };

  const allCategories = flattenCategories(categories);
  const filteredCategories = useMemo(
    () => filterCategoryTree(categories, search.trim()),
    [categories, search]
  );

  const handleCreate = () => {
    setSelectedCategory(null);
    setDialogOpen(true);
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setDialogOpen(true);
  };

  const handleManageQuestions = (category) => {
    setQuestionsCategory(category);
    setQuestionsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        await dispatch(deleteCategory(id)).unwrap();
        toast({
          title: "Success",
          description: "Category deleted successfully",
        });
        // Refetch categories after successful delete
        dispatch(fetchCategories());
      } catch (err) {
        toast({
          title: "Error",
          description: err || "Failed to delete category",
          variant: "destructive",
        });
      }
    }
  };

  const renderCategoryTree = (items, level = 0) => {
    return items.map((category) => {
      const hasChildren = category.children && category.children.length > 0;

      return (
        <div key={category.id} className={`${level > 0 ? "ml-6" : ""}`}>
          <div className="flex items-center justify-between p-4 border rounded-lg mb-2 hover:bg-accent/50 transition-colors">
            <div className="flex items-center gap-3 flex-1">
              <FolderTree className="w-5 h-5 text-muted-foreground" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium">
                    {category.name?.en || category.name}
                  </h3>
                  {category.is_active !== false && (
                    <Badge variant="success" className="text-xs">
                      Active
                    </Badge>
                  )}
                  {hasChildren && (
                    <Badge variant="secondary" className="text-xs">
                      {category.children.length}{" "}
                      {category.children.length === 1
                        ? "subcategory"
                        : "subcategories"}
                    </Badge>
                  )}
                  {(category.question_count || 0) > 0 && (
                    <Badge variant="outline" className="text-xs">
                      {category.question_count} question
                      {category.question_count === 1 ? "" : "s"}
                    </Badge>
                  )}
                </div>
                {category.description?.en && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {category.description.en}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleManageQuestions(category)}
              >
                <List className="w-4 h-4 mr-1" />
                Questions
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleEdit(category)}
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleDelete(category.id)}
              >
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
              {hasChildren && (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
          </div>
          {hasChildren && renderCategoryTree(category.children, level + 1)}
        </div>
      );
    });
  };

  if (loading && categories.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Categories</h1>
          <p className="text-muted-foreground">
            Manage question categories and subcategories
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Create Category
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Category Tree</CardTitle>
          <CardDescription>
            Hierarchical view of all categories and their subcategories
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search categories…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          {categories.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FolderTree className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No categories found</p>
              <Button variant="link" onClick={handleCreate} className="mt-2">
                Create your first category
              </Button>
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>No categories match “{search.trim()}”</p>
            </div>
          ) : (
            <div className="space-y-2">
              {renderCategoryTree(filteredCategories)}
            </div>
          )}
        </CardContent>
      </Card>

      <CategoryDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={() => dispatch(fetchCategories())}
        category={selectedCategory}
        categories={allCategories}
      />

      <CategoryQuestionsDialog
        open={questionsDialogOpen}
        onOpenChange={(open) => {
          setQuestionsDialogOpen(open);
          if (!open) {
            setQuestionsCategory(null);
            dispatch(fetchCategories());
          }
        }}
        category={questionsCategory}
      />
    </div>
  );
}

export default Categories;
